import moment from "moment";

import { InternalPatientResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/InternalPatientResponse";
import { LabActionTaken, MyleActionDTO, MyleNoteDTO, ObservationDTO, RequestDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { AttachedCommunicationsPopup } from "@/Common/Components/AttachedCommunications/AttachedCommunicationsPopup";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import { FixedClickableTextPanel } from "@/Common/Components/ClickableText/FixedClickableTextPanel";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { KendoComboBox } from "@/Common/Components/Inputs/KendoComboBox";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { LinkPatientToLabPopup } from "@/Common/Components/Labs/LinkPatientToLabPopup";
import { LinkingPopupService } from "@/Common/Components/Modals/LinkingPopup/LinkingPopupService";
import { CreatePatientPopup } from "@/Common/Components/Patient/CreatePatientPopup";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { ReferalDoctorSearchAutocompleteCustom } from "@/Common/Components/Search/ReferalDoctorSearchAutocompleteCustom";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { NameHelper, NameHelperFormat } from "@/Common/Helpers/NameHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { SecondScreenHelper } from "@/Common/Helpers/SecondScreenHelper";
import { AttachedCommunication } from "@/Common/Models/AttachedCommunication/AttachedCommunication";
import { AttachedCommunicationType } from "@/Common/Models/AttachedCommunication/AttachedCommunicationType";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import { MessageOrTaskContext } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ExternalMessageFormPopupComponent } from "@/Communications/Components/ExternalCommunications/Form/ExternalMessageFormPopupComponent";
import { DirectoryProfessionalSearchLockingComponent } from "@/ContactDirectory/Components/DirectoryProfessionalSearchLockingComponent";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models/DirectoryHealthCareProvider";
import { ILabViewProps, LabViewState } from "@/Labs/Components/LabView";
import { LabsHelper } from "@/Labs/Helpers/LabsHelper";
import { LabMessage } from "@/Labs/Models/LabMessage";
import { ILabModel } from "@/Labs/Models/UI/ILabModel";
import { ILabsInboxModel } from "@/Labs/Models/UI/ILabsInboxModel";
import { ILabsTabModel } from "@/Labs/Models/UI/ILabsTabModel";
import { ISideNoteModel } from "@/Labs/Models/UI/ISideNoteModel";
import { SendResultStrategy } from "@/Labs/Services/Portal/Strategies/SendResultStrategy";
import { MedicalSummaryPanelAction } from "@/Patient/Actions/MedicalSummaryPanelAction";
import { MedicalSummaryPanel } from "@/Patient/Components/MedicalSummaryPanel/MedicalSummaryPanel";
import { MedicalSummaryPanelContext } from "@/Patient/Components/MedicalSummaryPanel/MedicalSummaryPanelEnums";
import { SummaryPanelButtons } from "@/Patient/Components/MedicalSummaryPanel/SummaryPanelButtons";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";

import styles from "./PatientLabView.module.scss";

import img_icon_labchart from "@/Globals/_images/icons/LabChart.png";

interface IPatientLabViewProps {
    Model: ILabModel;
    InboxModel: ILabsInboxModel;
    SideNoteModel: ISideNoteModel;
    LabTabModel: ILabsTabModel
    isReadOnly?: boolean
}

class PatientLabView extends BaseReactComponent<IPatientLabViewProps, LabViewState> {
    private readonly isPortalEnabled: boolean = cacheStore.ClinicSettings_Portal.EnablePortalWebsite;
    private readonly isPortalInvitationFlowV2Enabled: boolean = cacheStore.ClinicSettings.EnablePortalInvitationFlowV2;
    private readonly hvarValue: string;

    private myleHelper: MyleHelper = new MyleHelper();
    private myleInfoPatient: Patient = null;
    private LabFillerID: string = null;

    private consultationRef: React.RefObject<HTMLDivElement> = React.createRef();
    private followUpAndPlanRef: React.RefObject<HTMLDivElement> = React.createRef();

    public constructor(props: IPatientLabViewProps) {
        super(props);

        const initialState = new LabViewState();
        initialState.isMedicalSummaryPanelOpen = !!cacheStore?.UserSettings.LabViewMedicalSummaryPanelSettings?.IsMedicalSummaryPanelOpen;
        this.state = initialState;
        this.hvarValue = (window.innerWidth <= 1024) ? "-150" : "-100";
    }

    public render(): JSX.Element {
        const labMessage = this.props.Model.get("LabMessage");
        const isManualLabEntry = labMessage.Model.IsManualLabEntry;
        const canFileAndHold = sessionStore.canApproveDocument() && !this.props.isReadOnly && !isManualLabEntry;

        this.myleInfoPatient = this.props.Model.get("MylePatient");

        this.LabFillerID = labMessage.Model.LabFillerID;
        let IsExpandedNote = canFileAndHold && this.props.Model.get("IsNoteExpanded");

        let selectedPatient = this.props.Model.get("SelectedPersonForLinkingLab");
        let selectedPatinetForLinkHasFullInfo = false;
        if (selectedPatient != null && selectedPatient.Patient != null)
            selectedPatinetForLinkHasFullInfo = true;

        let patientDDN = "";

        if (labMessage.Model.Patient.DateOfBirth != null) {
            patientDDN = moment(labMessage.Model.Patient.DateOfBirth).format("YYYY-MM-DD") + " ";
        }

        const showAssociationControl = this.myleInfoPatient == null;

        const defaultLinkPatientInputText = LabsHelper.GetDefaultPatientNameForLinkPatient(patientDDN, labMessage.Model.Patient.FirstName, labMessage.Model.Patient.LastName);

        return (
            <div className="labs-container-width-workspace">
                {this.props.InboxModel.get("IsSendToPatientPortalPopupOpen") && (
                    <SendToPortalPopup
                        model={this.props.InboxModel.get("PortalLinkedItemModel")}
                        strategy={new SendResultStrategy()}
                    />
                )}

                {this.props.Model.get("AttachedCommunicationsPopupOpen") && (
                    <AttachedCommunicationsPopup
                        controlId="attached_communications_labview"
                        context={AttachedCommunicationType.Task}
                        leftPosition={this.props.Model.get("AttachedCommunicationsPopupX")}
                        topPosition={this.props.Model.get("AttachedCommunicationsPopupY")}
                        handleRedirect={ac => this.onAttachedCommunicationClick(ac)}
                        handleClose={() => this.onAttachedCommunicationClose()}
                        attachedCommunications={this.props.Model.get("IndividualTaskLinkedItems")}
                    />
                )}

                {this.props.Model.get("ShowExternalCommunicationPopup") &&
                    this.renderExternalCommunicationPopup(this.myleInfoPatient)
                }

                <div className="sb-workspace inherit-height">
                    {(!this.props.isReadOnly && !labMessage.Model.IsManualLabEntry)
                        ? this.renderHeaderForLabsView(showAssociationControl, labMessage)
                        : (<div className={`myle-subheader bottom-border ${styles.patientLabHeader}`} />)
                    }

                    <div className="fxheight hide-overflow" data-hvar={this.hvarValue}>
                        <div className="main-content myle-form labs">
                            <div className="labs-view">
                                {this.renderLeftSideOfPage(labMessage)}

                                {!isManualLabEntry && IsExpandedNote && this.myleInfoPatient != null && (
                                    <div className="tabcontrol fixed" onClick={this.collapseLeftMenu.bind(this)}>
                                        <div className="slide-menu-icon blue">
                                            <span />
                                            <span />
                                            <span />
                                        </div>
                                    </div>
                                )}
                                {!isManualLabEntry && !(IsExpandedNote && this.myleInfoPatient) && !this.props.isReadOnly && (
                                    <div className="tabcontrol fixed" onClick={this.expandLeftMenu.bind(this)}>
                                        <div className="slide-menu-icon blue">
                                            <span />
                                            <span />
                                            <span />
                                        </div>
                                    </div>
                                )}

                                {!isManualLabEntry && !this.props.isReadOnly && this.renderRightSideOfPage(IsExpandedNote, labMessage)}

                            </div>
                        </div>
                    </div>

                    {this.renderFooterForLabsView(IsExpandedNote, labMessage, canFileAndHold)}
                    {this.props.Model.get("ShowLinkPatientPopup") && (
                        <LinkPatientToLabPopup
                            defaultText={defaultLinkPatientInputText}
                            isLinkButtonDisable={!selectedPatinetForLinkHasFullInfo}
                            onLinkPatient={() => sessionStore.dispatch(LocalOperations.LabsUI.LinkSelectedPatient)}
                            onCancel={() => sessionStore.dispatch(LocalOperations.LabsUI.ToggleLinkPatientPopup)}
                            onToggleCreatePatientPopupClick={() => this.toggleCreatePatientPopupClick()}
                        />
                    )}

                    {this.props.Model.get("ShowCreatePatientPopup") && this.renderCreatePatientPopup()}
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        $(window).trigger("resize"); // Resize for when people switch tab and come back

        var labfillerId = this.props.Model.get("LabMessage").Model.LabFillerID;
        var cleanLabFillerId = this.getlabFillerId(labfillerId);
        sessionStore.lastFocusedClickableTextController = "lab_note_Laboratoire" + cleanLabFillerId;
    }

    public shouldComponentUpdate(nextProps: ILabViewProps, nextState: any): boolean {
        const isModelChanged = (this.props.Model !== nextProps.Model);
        const isSideNoteModelChanged = (this.props.SideNoteModel !== nextProps.SideNoteModel);
        const isLabTabModelChanged = (this.props.LabTabModel !== nextProps.LabTabModel);
        const isInboxModelChanged = (this.props.InboxModel !== nextProps.InboxModel);
        const isStateChanged = (this.state !== nextState);

        if (isModelChanged && this.props.Model.get("LabMessage").Model.LabFillerID != nextProps.Model.get("LabMessage").Model.LabFillerID) {
            const labfillerId = nextProps.Model.get("LabMessage").Model.LabFillerID;
            const cleanLabFillerId = this.getlabFillerId(labfillerId);
            sessionStore.lastFocusedClickableTextController = "lab_note_Laboratoire" + cleanLabFillerId;
        }

        return isModelChanged || isSideNoteModelChanged || isLabTabModelChanged || isInboxModelChanged || isStateChanged;
    }

    public componentDidUpdate(prevProps: ILabViewProps): void {
        $(window).trigger("resize");

        // ensure we scroll sections back to top when changing lab
        if (this.props.Model !== null && this.props.Model.get("LabMessage") != null && this.props.Model.get("LabMessage").Model != null &&
            prevProps.Model !== null && prevProps.Model.get("LabMessage") != null && prevProps.Model.get("LabMessage").Model != null) {
            if (this.props.Model.get("LabMessage").Model.LabFillerID !== prevProps.Model.get("LabMessage").Model.LabFillerID) {
                window.setTimeout(() => {
                    $("#labs-leftContent").scrollTop(0);
                }, 0);

                window.setTimeout(() => {
                    $("#control_div_lab").scrollTop(0);
                }, 100);
            }
        }
    }

    private renderHeaderForLabsView(showAssociationControl: boolean, labMessage: LabMessage): JSX.Element {
        return (
            <div className="myle-subheader bottom-border">
                {showAssociationControl === false && (
                    <div>
                        <div>
                            <span>
                                <a onClick={() => this.onNewTaskClick()}>
                                    {cacheStore.GetString("NewTaskLabel")}
                                </a>
                                {this.props.Model.get("IndividualTaskLinkedItems").any() && (
                                    <span
                                        style={{ marginLeft: "5px", cursor: "pointer" }}
                                        onClick={e => this.onTaskTrailClick(e)}
                                        className={this.props.Model.get("AreAllTasksCompleted")
                                            ? "circle-green"
                                            : "circle-orange"
                                        }
                                    >
                                        {this.props.Model.get("IndividualTaskLinkedItems").length}
                                    </span>
                                )}
                            </span>
                            {cacheStore.ClinicSettings.EnableExternalCommunication && (
                                <div style={{ display: "inline" }}>
                                    <span>{" / "}</span>
                                    <span>
                                        <a onClick={() => this.showExternalCommunicationPopup()}>
                                            {cacheStore.GetString("NewExternalCommunicationLabel")}
                                        </a>
                                    </span>
                                </div>
                            )}
                            <span>{" / "}</span>
                            <HyperLinkControl operation={LocalOperations.LabsUI.ToggleLinkPatientPopup} text={cacheStore.GetString("RelinkPatientLabel")} />
                        </div>
                    </div>
                )}
                {showAssociationControl && (
                    <div className="row no-side-margin">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <div className="tb pateint-header">
                                <div className="tbc r1">
                                    <h4>{NameHelper.formatFullNameForHasName(labMessage.Model.Patient, NameHelperFormat.FirstNameFirst)}</h4>
                                    <ul className="no-margin">
                                        <li><strong>{sessionStore.getDefaultHealthCardLabel()}</strong><span>{labMessage.Model.Patient.NAM}</span></li>
                                        <li><strong>Gender</strong><span>{labMessage.Model.Patient.Sex}</span></li>
                                        <li><strong>{cacheStore.GetString("DateofBirthLabel")} </strong><span>{moment(labMessage.Model.Patient.DateOfBirth).format("YYYY/MM/DD")}</span></li>
                                        <li> <HyperLinkControl operation={LocalOperations.LabsUI.ToggleLinkPatientPopup} text={cacheStore.GetString("LinkLabel")} /></li>
                                    </ul>
                                </div>
                                <div className="tbc r2">
                                    <ul className="no-margin">
                                        {labMessage.Model.Patient.Phones.map(phone => (
                                            <li key={phone.Number}>
                                                <span>{phone.Number}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private renderLeftSideOfPage(labMessage: LabMessage): JSX.Element {
        var cssForDetailsArrow = "down";
        if (this.props.Model.get("ShowAdditionalDetails") === true) { cssForDetailsArrow = "up"; }

        var cssForAbnormalAndCriticalOberservationArrow = "down";
        if (this.props.Model.get("ShowAbnormalAndCriticalObservations") === true) { cssForAbnormalAndCriticalOberservationArrow = "up"; }

        let lab = this.props.Model.get("LabMessage");
        let maxPrelevement = labMessage.Model.Order.RequestsList.sortByPropertyDesc(x => x.MaxObservationDatetime_CollectedDatetime).firstOrDefault()?.MaxObservationDatetime_CollectedDatetime;

        let observationRange = toDateRange(labMessage.Model.FirstObservation, labMessage.Model.LastObservation);
        let prelevmentDateTime = toDateRange(labMessage.Model.PrelevmentDateTime ?? maxPrelevement, maxPrelevement);
        let medfarReceptionRange = toDateRange(labMessage.Model.FirstInsertInARLMYLEDateTime, labMessage.Model.LastInsertInARLMYLEDateTime);

        const requestNumberToDisplay = labMessage.Model.IsManualLabEntry ? labMessage.Model.RequestNumber : labMessage.Model.LabFillerID;

        return (
            <div className="left" style={{ width: "100%" }}>
                <div className="vertical-scroll-only" id="labs-leftContent">
                    <div className={styles.labHeaderContainer}>
                        {cacheStore.ClinicSettings.EnableResultsFixes && lab.Model.Detail && (
                            <div className={styles.labDetailsContainer}>
                                <span title={lab.Model.Detail}>
                                    {lab.Model.Detail}
                                </span>
                            </div>
                        )}
                        <div>
                            {this.formatLabHeaderStatus(labMessage.Model.ActionTakenByUnknowSplitted)}
                        </div>
                    </div>

                    <div className="row no-side-margin">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <div className="myle-toggle-btn" onClick={this.toggleAdditionalInfo.bind(this)}>
                                <i className={cssForDetailsArrow} />
                                <span>{cacheStore.GetString("AdditionalinformationLabel")}</span>
                                <div className="pull-right" onClick={function(e: React.MouseEvent<HTMLDivElement>): void { e.stopPropagation(); }}>
                                    {this.formatEncapsolatedData(labMessage)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {this.props.Model.get("ShowAdditionalDetails") && (
                        <div className="row" style={{ margin: "0 0 0 10px" }}>
                            <div className="col-lg-5 col-md-5 col-sm-5 text-left">
                                <table className="basic-table labs">
                                    <tbody>
                                        {this.getColumn(0, cacheStore.GetString("OriginLabel"), labMessage.Model.SendingFacility)}
                                        {this.getColumn(1, cacheStore.GetString("ContextLabel"), labMessage.Model.Context)}
                                        {this.getColumn(2, cacheStore.GetString("RequestsLabel") + " No.#", requestNumberToDisplay)}
                                        {this.getColumn(3, cacheStore.GetString("EmissionLabel"), !IsNullOrWhiteSpace(labMessage.Model.EmittedDateTime) ? moment(labMessage.Model.EmittedDateTime).format("YYYY/MM/DD HH:mm:ss A") : "--")}
                                        {this.getColumn(4, cacheStore.GetString("MyleReceptionDate"), medfarReceptionRange)}
                                        {this.getColumn(5, cacheStore.GetString("RequestedByLabel"), labMessage.getFormattedPrescriptor())}
                                        <tr key={6}>
                                            <td className="c1">
                                                <span>C.C</span>
                                            </td>
                                            <td className="c2">
                                                {labMessage.getFormattedCC().map(name => (
                                                    <div key={name}><strong>{name}</strong></div>
                                                ))}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="col-lg-7 col-md-7 col-sm-7">

                                <table className="basic-table labs">
                                    <tbody>
                                        {this.getColumn(0, cacheStore.GetString("CollectionDateLabel"), prelevmentDateTime)}
                                        {this.getColumn(1, cacheStore.GetString("SpecimenReceivedDateLabel"), DateHelper.formatDate(labMessage.Model.SpecimenReceptionDateTime, "YYYY/MM/DD HH:mm:ss A") || "--")}
                                        {this.getColumn(2, cacheStore.GetString("ObservationsLabel"), observationRange)}
                                    </tbody>
                                </table>
                            </div>
                            <div className="row no-side-margin" />
                        </div>
                    )}

                    <div className="row no-side-margin">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <div className="myle-toggle-btn" onClick={this.ToggleAbnormalAndCriticalInfo.bind(this)}>
                                <i className={cssForAbnormalAndCriticalOberservationArrow} />
                                <span>{cacheStore.GetString("AbnormalObservationsMLabel")}</span>
                            </div>
                        </div>
                    </div>

                    {this.props.Model.get("ShowAbnormalAndCriticalObservations") && (
                        <div className="row no-side-margin">
                            <div className="col-lg-12">
                                <table className="table lab-view-tb">
                                    <thead>
                                        <tr className="noborder">
                                            <th>{cacheStore.GetString("AnalyseLabel")}</th>
                                            <th>{cacheStore.GetString("ResultsLabel")}</th>
                                            <th>{cacheStore.GetString("UnitsLabel")}</th>
                                            <th>{cacheStore.GetString("ReferenceShortlbl")}</th>
                                            <th>{cacheStore.GetString("FlagMLabel")}</th>
                                            <th>{cacheStore.GetString("GraphiqueShortlbl")}</th>
                                            {!this.props.isReadOnly && <th style={{ width: "80px" }}>{cacheStore.GetString("NotesLabel") + "/" + cacheStore.GetString("ActionsLabel")}</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.formatLabResults(labMessage.Model.Order.RequestsList, true)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="row no-side-margin">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <div className="myle-toggle-btn">
                                <div className="flex-container">
                                    <div><span>{cacheStore.GetString("FullResultsMLabel")}</span></div>
                                    {!this.props.isReadOnly && (
                                        <div className="flex-container" style={{ justifyContent: "flex-start", marginRight: "25px" }}>
                                            <div style={{ marginRight: "4px" }}>{cacheStore.GetString(cacheStore.ClinicSettings.EnableResultsFixes ? "ResultDetailsLabel" : "NotesDLabel")}</div>
                                            {lab.Model.ParentLevel_MyleNoteDtos.length == 0 && (
                                                <div>
                                                    <a
                                                        onClick={e =>
                                                            cacheStore.ClinicSettings.EnableResultsFixes ?
                                                                this.globalResultDetailClick(this.LabFillerID, lab.Model.ParentLevel_MyleNoteDtos, lab.Model.Detail, e) :
                                                                this.globalNotesClick(this.LabFillerID, lab.Model.ParentLevel_MyleNoteDtos, e)
                                                        }
                                                        className="notes_dropdown_main pointer"
                                                    >[<span className="red-text"> + </span>]
                                                    </a>
                                                </div>
                                            )}
                                            {lab.Model.ParentLevel_MyleNoteDtos.length > 0 && (
                                                <div>
                                                    <a
                                                        onClick={e =>
                                                            cacheStore.ClinicSettings.EnableResultsFixes ?
                                                                this.globalResultDetailClick(this.LabFillerID, lab.Model.ParentLevel_MyleNoteDtos, lab.Model.Detail, e) :
                                                                this.globalNotesClick(this.LabFillerID, lab.Model.ParentLevel_MyleNoteDtos, e)
                                                        }
                                                        className="notes_dropdown_main pointer"
                                                    >[<strong className="red-text"> {lab.Model.ParentLevel_MyleNoteDtos.length} </strong>]
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row no-side-margin">
                        <div className="col-lg-12">
                            {labMessage.Model.Patient.NotesList.map((patientNote, index) => (
                                patientNote.Comment && (
                                    <div key={`${patientNote.Comment} ${index}`}>
                                        <span className="paddedx2" style={{ fontFamily: "monospace" }}>{patientNote.Comment.replace(/\n\n\n\n/g, "\n").replace(/\n\n/g, "\n")}</span>
                                    </div>
                                )
                            ))}
                            <table className="table lab-view-tb">
                                <thead>
                                    <tr className="noborder">
                                        <th>{cacheStore.GetString("AnalyseLabel")}</th>
                                        <th>{cacheStore.GetString("ResultsLabel")}</th>
                                        <th>{cacheStore.GetString("UnitsLabel")}</th>
                                        <th>{cacheStore.GetString("ReferenceShortlbl")}</th>
                                        <th>{cacheStore.GetString("FlagMLabel")}</th>
                                        <th>{cacheStore.GetString("GraphiqueShortlbl")}</th>
                                        {!this.props.isReadOnly && <th style={{ width: "80px" }}>{cacheStore.GetString("NotesLabel") + "/" + cacheStore.GetString("ActionsLabel")}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.formatLabResults(labMessage.Model.Order.RequestsList, false)}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderRightSideOfPage(IsExpandedNote: boolean, labMessage: LabMessage): JSX.Element {
        var cleanLabFillerId = this.getlabFillerId(labMessage.Model.LabFillerID);

        return (
            <div className={"transition patient-lab-right" + (!IsExpandedNote || !this.myleInfoPatient ? " collapsed" : "")}>
                <div className={`row no-side-margin ${styles.marginBottom}`}>
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        <p>{sessionStore.getCurrentEmployee().nameWithPrefix() + " "}
                            <HyperLinkControl text={"(" + (labsStore.Model.get("CurrentSpeciality") ? labsStore.Model.get("CurrentSpeciality").GetValue() : "") + ")"} operation={LocalOperations.LabsUI.SiteNote_SpecialityToggle} />
                            {cacheStore.ClinicSettings.EnableSecondScreen && (
                                <SummaryPanelButtons
                                    isMedicalSummaryPanelOpen={this.state.isMedicalSummaryPanelOpen}
                                    onMedicalSummaryLinkClick={() => this.toggleMedicalSummaryPanel()}
                                    onPopoutClick={() => this.openSecondScreen()}
                                    cssClass="labsToggleContainer"
                                    hideOpenSummaryPanelLink
                                />
                            )}
                        </p>
                        <div className="display-flex-with-space-between">
                            <p>{moment(Date.now()).format("YYYY/MM/DD")}</p>
                        </div>
                    </div>
                </div>
                {cacheStore.ClinicSettings.EnableSecondScreen && (
                    <>
                        <div className={styles.medicalSummaryLink} onClick={() => this.toggleMedicalSummaryPanel()}>
                            <div className={styles.icon}>
                                <span className={`glyphicon ${this.state.isMedicalSummaryPanelOpen ? "glyphicon-chevron-up" : "glyphicon-chevron-down"}`} />
                            </div>
                            <label>{cacheStore.GetString("MedicalSummaryMLabel")}</label>
                        </div>
                        <div className={`animation-container-in-result ${this.state.isMedicalSummaryPanelOpen ? "open" : ""}`}>
                            <MedicalSummaryPanel
                                patientContactId={this.props.Model.get("MylePatient")?.IndividualResponse?.Contact_id}
                                cssClass="containerPatientLabView"
                                context={MedicalSummaryPanelContext.PatientLabTab}
                            />
                        </div>
                    </>
                )}

                <div id="control_div_lab">
                    <div className="row no-side-margin">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <div className="banner-label">
                                <span>{cacheStore.GetString("EvolutionaryNoteMLabel")}</span>
                            </div>
                        </div>
                    </div>

                    <div className="row no-side-margin">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <label>{cacheStore.GetString("ReasonLabel")}</label>
                            <TextAreaResizable
                                verticalResizeOnly={true}
                                setCursorStartOnFocus={true}
                                id={"lab_note_reason" + cleanLabFillerId}
                                text={this.props.SideNoteModel.get("Raison")}
                                isDisabled={false}
                                minHeightPx={35}
                                operation={LocalOperations.LabsUI.SiteNote_RaisonChange}
                                resetTextOnIdChange={true}
                            />
                            <div>
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="RFV"
                                    inputElementId={"lab_note_reason" + cleanLabFillerId}
                                    isLinkDisabled={false}
                                    isClickableTextPanelOpen={this.props.Model.get("ReasonClickableTextVisible")}
                                    onClickableTextPanelOpen={(): void => this.showClickableText("reason")}
                                    onClickableTextPanelClose={(): void => this.hideClickableText()}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row no-side-margin">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <label>{cacheStore.GetString("LaboratoryLabel")}</label>
                            <TextAreaResizable
                                verticalResizeOnly={true}
                                setCursorStartOnFocus={true}
                                id={"lab_note_Laboratoire" + cleanLabFillerId}
                                text={this.props.SideNoteModel.get("Laboratoire")}
                                isDisabled={false}
                                minHeightPx={70}
                                operation={LocalOperations.LabsUI.SiteNote_LaboratoireChange}
                                resetTextOnIdChange={true}
                            />
                            <div>
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="LAB"
                                    inputElementId={"lab_note_Laboratoire" + cleanLabFillerId}
                                    isClickableTextPanelOpen={this.props.Model.get("LabClickableTextVisible")}
                                    onClickableTextPanelOpen={(): void => this.showClickableText("lab")}
                                    onClickableTextPanelClose={(): void => this.hideClickableText()}
                                    isLinkDisabled={false}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row no-side-margin">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <label>{cacheStore.GetString("RadiologyLabel")}</label>
                            <TextAreaResizable
                                verticalResizeOnly={true}
                                setCursorStartOnFocus={true}
                                id={"lab_note_Imagerie" + cleanLabFillerId}
                                text={this.props.SideNoteModel.get("Imagerie")}
                                isDisabled={false}
                                minHeightPx={70}
                                operation={LocalOperations.LabsUI.SiteNote_ImagerieChange}
                                resetTextOnIdChange={true}
                            />
                            <div>
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="IMG"
                                    inputElementId={"lab_note_Imagerie" + cleanLabFillerId}
                                    isLinkDisabled={false}
                                    isClickableTextPanelOpen={this.props.Model.get("ImagingClickableTextVisible")}
                                    onClickableTextPanelOpen={(): void => this.showClickableText("imaging")}
                                    onClickableTextPanelClose={(): void => this.hideClickableText()}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row no-side-margin">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <label>{cacheStore.GetString("RequestsLabel")}</label>
                            <CheckboxControl label={cacheStore.GetString("UrgentLabel")} checked={this.props.SideNoteModel.get("UrgentRequete")} operation={LocalOperations.LabsUI.SiteNote_UrgentRequeteChange} DivWrap={true} />
                            <TextAreaResizable
                                verticalResizeOnly
                                setCursorStartOnFocus
                                id={"lab_note_requete" + cleanLabFillerId}
                                text={this.props.SideNoteModel.get("Requete")}
                                isDisabled={false}
                                minHeightPx={70}
                                operation={LocalOperations.LabsUI.SiteNote_RequeteChange}
                                resetTextOnIdChange
                                invalid={this.props.SideNoteModel.get("UrgentRequete") && !this.props.SideNoteModel.get("Requete")}
                            />
                            <div>
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="REQ"
                                    inputElementId={"lab_note_requete" + cleanLabFillerId}
                                    isLinkDisabled={false}
                                    isClickableTextPanelOpen={this.props.Model.get("RequestClickableTextVisible")}
                                    onClickableTextPanelOpen={(): void => this.showClickableText("request")}
                                    onClickableTextPanelClose={(): void => this.hideClickableText()}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row no-side-margin">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <label>{cacheStore.GetString("ConsultationsLabel")}</label>

                            <CheckboxControl label={cacheStore.GetString("UrgentLabel")} checked={this.props.SideNoteModel.get("UrgentConsultation")} operation={LocalOperations.LabsUI.SiteNote_UrgentConsultationChange} DivWrap={true} />

                            <div style={{ margin: "0 0 10px 0" }}>
                                <KendoComboBox
                                    controlId="doc_edit_sources"
                                    selectedItem={this.props.SideNoteModel.get("ConsultationSpeciality") || ""}
                                    placeholder={cacheStore.GetString("SpecialityLabel")}
                                    items={Immutable.List(cacheStore
                                        .FindLookupsByTypeCode("Specialization_def")
                                        .map(p => p.GetValue())
                                    )}
                                    changeOperation={LocalOperations.LabsUI.SiteNote_ConsultationSpecialityChange}
                                    invalid={this.isConsultationElementFilled() && !this.props.SideNoteModel.get("ConsultationSpeciality")}
                                />
                            </div>

                            <div style={{ margin: "0 0 10px 0" }}>
                                <FeatureFlagControl
                                    flag={cacheStore.ClinicSettings.EnableContactDirectory}
                                    activeFeature={(
                                        <DirectoryProfessionalSearchLockingComponent
                                            componentId="appointmentEditDialog-referralDoctorAdd"
                                            onItemSelected={item => sessionStore.dispatch(
                                                LocalOperations.LabsUI.SiteNote_ConsultationDestinataireSelect,
                                                {
                                                    value: new PersonDropdownItem({ Item: DirectoryHealthCareProvider.toPerson(item.entity as DirectoryHealthCareProvider) })
                                                }
                                            )}
                                            selectedItems={this.props.SideNoteModel
                                                ?.get("ConsultationDestinataire")
                                                ?.map(p => ({ Name: `${p.Name} ${p.Licence ? `(${p.Licence}) ` : ""}` }))
                                                .toArray() ?? []
                                            }
                                            onTextChange={text => sessionStore.dispatch(LocalOperations.LabsUI.SiteNote_ConsultationDestiantaireTextChanged, { value: text })}
                                            searchText={this.props.SideNoteModel.get("ConsultationDestinataireFreeText")}
                                            dataCy="contactDirectory-patientLabView-referralDoctorSearch"
                                            onRemoveItem={() => sessionStore.dispatch(LocalOperations.LabsUI.SiteNote_ConsultationDestinataireClear)}
                                            placeholder={cacheStore.GetString("RecipientLabel")}
                                        />
                                    )}
                                    fallbackFeature={(
                                        <ReferalDoctorSearchAutocompleteCustom
                                            useblurevent={true}
                                            focusOnLoad={false}
                                            disabled={false}
                                            displayList={true}
                                            selectedDoctors={this.props.SideNoteModel.get("ConsultationDestinataire")}
                                            selectionOp={LocalOperations.LabsUI.SiteNote_ConsultationDestinataireSelect}
                                            componentId={"add_lab_ConsultationReferal_" + cleanLabFillerId}
                                            clearOp={LocalOperations.LabsUI.SiteNote_ConsultationDestinataireClear}
                                            closeDropDownAfterSelection={true}
                                            changedTextValueEvent={LocalOperations.LabsUI.SiteNote_ConsultationDestiantaireTextChanged}
                                            resetValueOnDefaultValueChange={true}
                                            placeholder={cacheStore.GetString("RecipientLabel")}
                                        />
                                    )}
                                />
                            </div>
                            <div ref={this.consultationRef}>
                                <TextAreaResizable
                                    verticalResizeOnly
                                    setCursorStartOnFocus
                                    id={"lab_note_consult_" + cleanLabFillerId}
                                    text={this.props.SideNoteModel.get("Consultation")}
                                    isDisabled={false}
                                    minHeightPx={70}
                                    operation={LocalOperations.LabsUI.SiteNote_ConsultationChange}
                                    resetTextOnIdChange
                                    invalid={this.isConsultationElementFilled() && !this.props.SideNoteModel.get("Consultation")}
                                />
                            </div>
                            <div>
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="CONS"
                                    inputElementId={"lab_note_consult_" + cleanLabFillerId}
                                    isLinkDisabled={false}
                                    isClickableTextPanelOpen={this.props.Model.get("ConsultClickableTextVisible")}
                                    onClickableTextPanelOpen={(): void => this.showClickableText("consult")}
                                    onClickableTextPanelClose={(): void => this.hideClickableText()}
                                    textboxHeight={this.consultationRef.current?.clientHeight ?? 0}
                                    clickableTextPanelCssStyle={{ bottom: "0px" }}
                                    isAboveLabel
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row no-side-margin">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <label>{cacheStore.GetString("OtherPlanNoteLbl")}</label>
                            <div ref={this.followUpAndPlanRef}>
                                <TextAreaResizable
                                    verticalResizeOnly={true}
                                    setCursorStartOnFocus={true}
                                    id={"lab_note_FollowUp" + cleanLabFillerId}
                                    text={this.props.SideNoteModel.get("FollowUp")}
                                    isDisabled={false}
                                    minHeightPx={70}
                                    operation={LocalOperations.LabsUI.SiteNote_FollowUpChange}
                                    resetTextOnIdChange={true}
                                />
                            </div>
                            <div>
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="OTH"
                                    inputElementId={"lab_note_FollowUp" + cleanLabFillerId}
                                    isLinkDisabled={false}
                                    isClickableTextPanelOpen={this.props.Model.get("FollowUpPlanClickableTextVisible")}
                                    onClickableTextPanelOpen={(): void => this.showClickableText("followupplan")}
                                    onClickableTextPanelClose={(): void => this.hideClickableText()}
                                    textboxHeight={this.followUpAndPlanRef.current?.clientHeight ?? 0}
                                    clickableTextPanelCssStyle={{ bottom: "0px" }}
                                    isAboveLabel
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderLowerRightButtons(isOnHold: boolean, isOpenAproveBottomDisabled: boolean): JSX.Element {
        let OpenButton, ApprouveButton;
        if (isOnHold) {
            ApprouveButton = (
                <ButtonControl
                    className="btn btn-primary"
                    disabled={isOpenAproveBottomDisabled}
                    onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.PutOnHold_ApprouveEncounter)}
                    label={cacheStore.GetString("OnHoldLabAction") + " + " + cacheStore.GetString("ApproveLabel")}
                />
            );
            OpenButton = (
                <ButtonControl
                    className="btn btn-primary push-right-5"
                    disabled={isOpenAproveBottomDisabled}
                    onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.PutOnHold_SaveAndOpenEncounter)}
                    label={cacheStore.GetString("OnHoldLabAction") + " + " + cacheStore.GetString("OpenLabel")}
                />
            );
        }
        else {
            OpenButton = (
                <ButtonControl
                    className="btn btn-primary push-right-5"
                    disabled={isOpenAproveBottomDisabled}
                    onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.File_SaveAndOpenEncounter)}
                    label={cacheStore.GetString("ClassifyLabel") + " + " + cacheStore.GetString("OpenLabel")}
                />
            );

            const approveButtonLabel = cacheStore.GetString("ClassifyLabel") + " + " + cacheStore.GetString("ApproveLabel");

            ApprouveButton = (
                <ButtonControl
                    className="btn btn-primary"
                    disabled={isOpenAproveBottomDisabled}
                    onClick={() => {
                        LinkingPopupService
                            .promptForAdditionalSignOffInformation(
                                this.props.Model.get("MylePatient").IndividualResponse.Contact_id,
                                sessionStore.getCurrentEmployee().getActiveSite().Model.Id,
                                null,
                                null
                            )
                            .then(info => {
                                sessionStore.dispatch(LocalOperations.LabsUI.File_ApprouveEncounter, { additionalSignOffInformation: info });
                            })
                            .catch(p => p && console.error(p)); // prompt cancelled
                    }}
                    label={approveButtonLabel}
                />
            );
        }

        return (
            <div>
                {OpenButton}
                {ApprouveButton}
            </div>
        );
    }

    private renderFooterForLabsView(IsExpandedNote: boolean, labMessage: LabMessage, canFileAndHold: boolean): JSX.Element {
        let portalSyncButton: JSX.Element = null;

        if (this.isPortalEnabled &&
            (this.myleInfoPatient?.IsRegisteredToPortal() || this.isPortalInvitationFlowV2Enabled)
            && labMessage.Model.HasEncapsulatedData
            && !this.props.isReadOnly) {
            const buttonProps = PortalHelper.getPortalButtonPropsForShareableItem(labMessage);

            portalSyncButton = (
                <PortalStatusButton
                    className="push-left-5"
                    icon="share"
                    style={{ display: "inline-block" }}
                    onClick={() => this.PortalStatusClicked(labMessage)}
                    {...buttonProps}
                />
            );
        }

        const isOpenAproveBottomDisabled = !this.myleInfoPatient || this.props.Model.get("IsSaving") || !this.isConsultationSectionValid() || !this.isLabRequestSectionValid();

        return (
            <div>
                {IsExpandedNote && this.myleInfoPatient != null && (
                    <div className="ws-subfooter flex-container" style={{ "paddingBottom": "6px" }}>
                        <div className="flex-container mid" style={{ alignItems: "flex-end" }}>
                            <div className="c1" style={{ paddingTop: "2px" }}>
                                <ButtonControl
                                    className="btn btn-primary push-right-5"
                                    onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.PreviousLab)}
                                    disabled={labsStore.LabFillerIDsViewedNaviguationPosition == 0}
                                    label="<"
                                />
                                <ButtonControl
                                    className="btn btn-primary push-right-5"
                                    onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.NextLab)}
                                    label=">"
                                />
                            </div>

                            {canFileAndHold && (
                                <div className="c2">
                                    <div className="flex-container right" style={{ alignItems: "flex-end" }}>
                                        <ButtonControl
                                            className="btn btn-default push-right-5"
                                            id={labMessage.Model.LabFillerID}
                                            onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.HoldLab, { id: labMessage.Model.LabFillerID })}
                                            label={cacheStore.GetString("OnHoldLabAction")}
                                        />
                                        <ButtonControl
                                            className="btn btn-primary"
                                            id={labMessage.Model.LabFillerID}
                                            onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.FileLab, { id: labMessage.Model.LabFillerID })}
                                            label={cacheStore.GetString("ClassifyLabel")}
                                        />
                                        {portalSyncButton}
                                    </div>
                                </div>
                            )}
                        </div>
                        {!this.props.isReadOnly && (
                            <div className="flex-container end" style={{ alignItems: "flex-end" }}>
                                <div className="flex-container end" style={{ alignItems: "flex-end" }}>
                                    <CheckboxControl labelStyle={{ marginRight: "20px" }} checked={this.props.Model.get("OnHoldCheckBox")} operation={LocalOperations.LabsUI.ToggleHoldFileCheckBox} label={cacheStore.GetString("OnHoldLabAction")} />

                                    {this.renderLowerRightButtons(
                                        this.props.Model.get("OnHoldCheckBox")
                                        , isOpenAproveBottomDisabled
                                    )}
                                    <div style={{ display: "inline" }}>
                                        {portalSyncButton}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {!(IsExpandedNote && this.myleInfoPatient != null) && (
                    <div className="ws-subfooter flex-container" style={{ "paddingBottom": "6px", alignItems: "flex-end" }}>
                        <div className="c1" style={{ paddingTop: "2px" }}>
                            <ButtonControl
                                className="btn btn-primary push-right-5"
                                onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.PreviousLab)}
                                disabled={labsStore.LabFillerIDsViewedNaviguationPosition == 0}
                                label="<"
                            />
                            <ButtonControl
                                className="btn btn-primary push-right-5"
                                onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.NextLab)}
                                label=">"
                            />
                        </div>
                        {(canFileAndHold || labMessage.Model.IsManualLabEntry) && (
                            <div className="c2">
                                {(!labMessage.Model.IsManualLabEntry) && (
                                    <ButtonControl
                                        className="btn btn-default push-right-5"
                                        id={labMessage.Model.LabFillerID}
                                        onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.HoldLab, { id: labMessage.Model.LabFillerID })}
                                        label={cacheStore.GetString("OnHoldLabAction")}
                                    />
                                )}
                                <FeatureFlagControl
                                    activeFeature={(
                                        EmployeeTitleHelper.isDoctor(sessionStore.myleEmployeeTitle) && (
                                            <ButtonControl
                                                variant="brand"
                                                id={labMessage.Model.LabFillerID}
                                                onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.FileManualLab, { id: labMessage.Model.LabFillerID })}
                                                label={cacheStore.GetString("ClassifyLabel")}
                                            />
                                        )
                                    )}
                                    fallbackFeature={(
                                        <ButtonControl
                                            className="btn btn-primary"
                                            id={labMessage.Model.LabFillerID}
                                            onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.FileLab, { id: labMessage.Model.LabFillerID })}
                                            label={cacheStore.GetString("ClassifyLabel")}
                                        />
                                    )}
                                    flag={cacheStore.ClinicSettings.EnableManualEntryOfLabResults && labMessage.Model.IsManualLabEntry}
                                />
                                {portalSyncButton}
                            </div>
                        )}
                        {!canFileAndHold && !labMessage.Model.IsManualLabEntry && (
                            <div className="c2">
                                {portalSyncButton}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    private formatLabResults(labrequests: RequestDTO[], isAbnormal: boolean): JSX.Element[] {
        let labMessage = this.props.Model.get("LabMessage");

        var rows: JSX.Element[] = [];
        var rowsAbnormal: JSX.Element[] = [];

        let patient = this.props.Model.get("MylePatient");

        let previousCollectionDate = null;
        let previousSectionIdentifier = null;
        let previousObrContainerIsIrrelevant = false;
        let sortedLabRequest = labrequests.sortByProperty(x => x.SetId?.padLeft("0", 3));
        if (labMessage.Model.LabFillerID.toUpperCase().endsWith("SRALAB"))
            sortedLabRequest = labrequests.sortByPropertyDesc(x => x.SetId?.padLeft("0", 3));

        sortedLabRequest.forEach((requestElem, idx) => {
            let firstObxTitle = requestElem.ObservationsList.firstOrDefault()?.Text || "";
            let currentObrContainerIsIrrelevant = (firstObxTitle.startsWith(requestElem.USIT) || IsNullOrWhiteSpace(requestElem.USIT)) /* Obr Title empty or redondant */
                && requestElem.ObservationsList.length <= 1 /* Doesnt really represant a container */
                && !IsNullOrWhiteSpace(requestElem.SectionIdentifier); /* Can be associated to a parent container */

            let obrTitle = !currentObrContainerIsIrrelevant && requestElem.USIT || requestElem.SectionIdentifier || ""; // Add a bit of value by keeping parent container title instead (in case we are forced to keep the row)
            let hasSpecialCollectionDate = labMessage.Model.PrelevmentDateTime != requestElem.MaxObservationDatetime_CollectedDatetime && requestElem.MaxObservationDatetime_CollectedDatetime != null;

            let graphJsx = patient && requestElem.ObservationsList.some(obx => obx.OtherValidValues)
                ? (
                    <td
                        onClick={e => {
                            e.stopPropagation();
                            sessionStore.dispatch(LocalOperations.LabsUI.Graphs_ShowGraphs, { lab: requestElem, patientContactId: patient.IndividualResponse.Contact_id });
                        }}
                    >
                        <a>
                            <img
                                alt="LabChart"
                                src={img_icon_labchart}
                                ref="imglabchart"
                            />
                        </a>
                    </td>
                )
                : (<td />);

            let isDifferentFromPreviousObr = previousCollectionDate != requestElem.MaxObservationDatetime_CollectedDatetime || previousSectionIdentifier != requestElem.SectionIdentifier;

            let skipObr = previousObrContainerIsIrrelevant && currentObrContainerIsIrrelevant && !isDifferentFromPreviousObr;
            if (!skipObr) {
                if (idx > 0)
                    rows.push(
                        <tr key={"_rq" + idx} className="noborder">
                            <td style={{ height: "20px" }}><span> </span></td>
                        </tr>
                    );

                rows.push(
                    <tr key={"rq" + idx} className="noborder">
                        <td colSpan={3}>
                            <span className="requestHeader">
                                {obrTitle}
                                {hasSpecialCollectionDate &&
                                    <span className="requestHeaderDetail"> ({cacheStore.GetString("CollectedOnLabel")}: {moment(requestElem.MaxObservationDatetime_CollectedDatetime).format("YYYY/MM/DD HH:mm:ss A")}) </span>
                                }
                            </span>
                        </td>
                        <td>&nbsp; </td>
                        <td>&nbsp; </td>
                        {graphJsx}
                        <td>&nbsp; </td>
                    </tr>
                );
            }

            requestElem.ObservationsList.sortByProperty(x => x.SetId?.toString()?.padLeft("0", 3)).forEach((obx, idy) => {
                var actionHyperLink = (<a onClick={e => this.sendActionNoteOperation(LocalOperations.LabsUI.ActionNotesClick, obx, this.LabFillerID, e)} className="notes_dropdown_main">[<span className="bold-text red">{" + "}</span>]</a>);

                var notes_and_Actions = (
                    <td>{actionHyperLink}</td>
                );

                if (obx.MyleNotesList != null && obx.MyleNotesList.length > 0) {
                    var noteHyperLink = (<a onClick={e => this.sendActionNoteOperation(LocalOperations.LabsUI.NotesClick, obx, this.LabFillerID, e)} className="notes_dropdown_main pointer">[<strong className="red-text"> {obx.MyleNotesList.length} </strong>]</a>);

                    notes_and_Actions = (<td>{noteHyperLink} / {actionHyperLink}</td>);
                }

                // Actions
                var actionsDetails = (<td><span /></td>);
                if (obx.MyleActionsList != null && obx.MyleActionsList.length > 0) {
                    var comaSeperatedActions: JSX.Element[] = [];

                    obx.MyleActionsList.forEach((y: MyleActionDTO, ida: number) => {
                        var colorClass = y.CompletedOn == null ? "pointer red-text" : "pointer green-text";

                        comaSeperatedActions.push(
                            <div key={ida} className="nowrap marginright5">
                                <span onClick={e => this.sendActionDetailOperation(y, this.LabFillerID, e)} className={colorClass}>{cacheStore.GetLookupById(y.ActionLookupId).GetValue() + ";\r\n "}</span>
                            </div>
                        );
                    });

                    actionsDetails = (<td>{comaSeperatedActions}</td>);
                }

                let obxGraphJsx = patient && obx.OtherValidValues
                    ? (
                        <td
                            onClick={e => {
                                e.stopPropagation();
                                sessionStore.dispatch(LocalOperations.LabsUI.Graphs_ShowGraphs, { observationtype: obx, patientContactId: patient.IndividualResponse.Contact_id });
                            }}
                        >
                            <img
                                alt="LabChart"
                                className="pointer"
                                src={img_icon_labchart}
                                style={{ margin: "2px 0 0 3px" }}
                                ref="imglabchart"
                            />
                        </td>
                    )
                    : (<td />);

                let abnormality = obx.AbnormalityFlag || "";
                let status = obx.ResultStatus != "F" && obx.ResultStatus?.truncate(3) || "";
                let flags = abnormality;
                if (status)
                    flags = flags + `| ${status}`;

                // Observatio Row
                const valueToSendToTextControl = `${obx.Text} ${obx.Text ? ":" : ""} ${obx.ValueText ?? ""} ${obx.UnitsIdentifier ?? ""}`;
                const observationIsAbnormal = !IsNullOrWhiteSpace(obx.AbnormalityFlag) && obx.AbnormalityFlag != "N";

                rows.push(
                    <tr
                        key={`request${idx}_observation${idy}`}
                        className={observationIsAbnormal ? "pointer red-row bold" : "pointer"}
                        onClick={() => this.sendToTextControlArea(valueToSendToTextControl)}
                    >
                        <td style={{ "whiteSpace": "pre-wrap" }}><span className="pointer padded">{obx.Text}</span></td>
                        <td>{obx.ValueText} </td>
                        <td>{obx.UnitsIdentifier} </td>
                        <td>{obx.ReferenceRange} </td>
                        <td>{flags} </td>
                        {obxGraphJsx}
                        {/* TD */ !this.props.isReadOnly && notes_and_Actions /* TD */}
                        {/* TD */ !this.props.isReadOnly && actionsDetails /* TD */}

                    </tr>
                );
                if (observationIsAbnormal) {
                    rowsAbnormal.push(
                        <tr
                            key={`request${idx}_obx${idy}`}
                            className="pointer red-row bold"
                            onClick={() => this.sendToTextControlArea(valueToSendToTextControl)}
                        >
                            <td style={{ "whiteSpace": "pre-wrap" }}><span className="pointer padded">{obx.Text}</span></td>
                            <td>{obx.ValueText} </td>
                            <td>{obx.UnitsIdentifier} </td>
                            <td>{obx.ReferenceRange} </td>
                            <td>{flags} </td>
                            {obxGraphJsx}
                            {/* TD */ !this.props.isReadOnly && notes_and_Actions /* TD */}
                            {/* TD */ !this.props.isReadOnly && actionsDetails /* TD */}
                        </tr>
                    );
                }

                obx.NotesList.forEach((observationNote, idyn) => {
                    if (observationNote.Comment == null || observationNote.Comment == "")
                        return;

                    // Observation Note Row
                    rows.push(
                        <tr
                            key={`request${idx}_observation${idy}_note${idyn}`}
                            className="noborder"
                            onClick={() => this.sendToTextControlArea(observationNote.Comment)}
                        >
                            <td style={{ "whiteSpace": "pre-wrap" }} className="pointer" colSpan={7}>
                                <span className="paddedx2" style={{ fontFamily: "monospace" }}>{observationNote.Comment.replace(/\n\n\n\n/g, "\n").replace(/\n\n/g, "\n")}</span>
                            </td>
                        </tr>
                    );
                });
            });

            requestElem.NotesList.map((requestNote, idxn) => {
                // Request Note Row
                rows.push(
                    <tr
                        key={`request${idx}_note${idxn}`}
                        className="noborder"
                        onClick={() => this.sendToTextControlArea(requestNote.Comment)}
                    >
                        <td style={{ "whiteSpace": "pre-wrap" }} colSpan={7}>
                            <span className="pointer padded" style={{ fontFamily: "monospace" }}>{requestNote.Comment.replace(/\n\n\n\n/g, "\n").replace(/\n\n/g, "\n")}</span>
                        </td>
                    </tr>
                );
            });

            previousSectionIdentifier = requestElem.SectionIdentifier;
            previousCollectionDate = requestElem.MaxObservationDatetime_CollectedDatetime;
            previousObrContainerIsIrrelevant = requestElem.ObservationsList.length <= 1;
        });

        return isAbnormal ? rowsAbnormal : rows;
    }

    private formatEncapsolatedData(labMessage: LabMessage): JSX.Element[] {
        var rows: JSX.Element[] = [];

        if (labMessage.Model.LinkedEmptyEncapsulatedData != null) {
            labMessage.Model.LinkedEmptyEncapsulatedData.forEach((Attachment, idx) => {
                var filename = cacheStore.GetString("AttachmentLabel");
                if (Attachment.FileName != null && Attachment.FileName.length > 0)
                    filename = Attachment.FileName;

                filename += "-" + (idx + 1) + "." + Attachment.Extension;

                var AttachementJSX = (<HyperLinkControl key={"HyperlinkAttachment_" + idx} text={filename} marginRight="15px" id={Attachment.Guid} operation={LocalOperations.LabsUI.OpenAttachment} />);
                rows.push(AttachementJSX);
            });
        }
        return rows;
    }

    private formatLabHeaderStatus(labActions: LabActionTaken[]): JSX.Element[] {
        var rows: JSX.Element[] = [];

        labActions.forEach((labAction, idx) => {
            var rowClass = (idx == labActions.length - 1) ? "row no-side-margin" : "row clear-margin-bottom no-side-margin"; // If last row keep margintie

            var actionResolved = "";
            if (labAction.Action == "Submit")
                actionResolved = cacheStore.GetString("SubmittedbyLabel");
            else
                actionResolved = cacheStore.GetString("FilebyLabel");

            rows.push(
                <div key={idx} className={rowClass}>
                    <div className="col-lg-12 col-md-12 col-sm-12 text-center">
                        <strong><span style={{ color: "#ff0000" }} className="red-text">{actionResolved} {labAction.Doctor} ({labAction.DoctorLicence}) - {moment(labAction.ActionDateTime).format("YYYY/MM/DD HH:mm:ss A")}</span></strong>
                    </div>
                </div>
            );
        });

        return rows;
    }

    private getColumn(id: number, a: string, b: string): JSX.Element {
        return (
            <tr key={id}>
                <td className="c1">
                    <span>{a}</span>
                </td>
                <td className="c2">
                    <strong>{b}</strong>
                </td>
            </tr>
        );
    }

    private toggleAdditionalInfo(): void {
        LocalEvent.Dispatch(LocalOperations.LabsUI.ToggleAdditionalInfo);
    }

    private ToggleAbnormalAndCriticalInfo(): void {
        LocalEvent.Dispatch(LocalOperations.LabsUI.ToggleAbnormalAndCriticalInfo);
    }

    private expandLeftMenu(): void {
        LocalEvent.Dispatch(LocalOperations.LabsUI.ExpandLeftMenu);
    }

    private collapseLeftMenu(): void {
        this.hideClickableText();
        LocalEvent.Dispatch(LocalOperations.LabsUI.CollapseLeftMenu);
    }

    private globalNotesClick(labFillerId: string, notes: MyleNoteDTO[], e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        var operation = new LocalEvent(LocalOperations.LabsUI.NotesClick);
        operation.AddItem("labFillerId", labFillerId);
        operation.AddItem("notes", notes);
        operation.AddItem("actions", []);
        operation.AddItem("event", e);
        LocalEventDispatcher.dispatch(operation);
    }

    private globalResultDetailClick(
        labFillerId: string,
        notes: MyleNoteDTO[],
        detail: string,
        event: React.MouseEvent
    ): void {
        event.preventDefault();
        event.stopPropagation();

        sessionStore.dispatch(LocalOperations.LabsUI.ResultDetailClick, {
            labFillerId,
            notes,
            actions: [],
            isGlobal: true,
            detail,
            event,
            isManualLabEntry: this.props.Model.get("LabMessage").Model.IsManualLabEntry
        });
    }

    private sendActionNoteOperation(clickOperationId: LocalOperations.LabsUI, obx: ObservationDTO, labFillerId: string, e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        var operation = new LocalEvent(clickOperationId);
        operation.AddItem("labFillerId", labFillerId);
        operation.AddItem("observation", obx);
        operation.AddItem("notes", obx.MyleNotesList);
        operation.AddItem("actions", obx.MyleActionsList);
        operation.AddItem("event", e);
        LocalEventDispatcher.dispatch(operation);
    }

    private sendActionDetailOperation(actionItem: MyleActionDTO, labFillerId: string, e: React.MouseEvent): void {
        e.stopPropagation();
        var operation = new LocalEvent(LocalOperations.LabsUI.ActionDetails_Click);
        operation.AddItem("labFillerId", labFillerId);
        operation.AddItem("actionItem", actionItem);
        operation.AddItem("event", e);
        LocalEventDispatcher.dispatch(operation);
    }

    private sendToTextControlArea(value: string): void {
        var operation = new LocalEvent(LocalOperations.Misc.ClickableTextControlAppend);
        operation.AddItem("value", value);

        LocalEventDispatcher.dispatch(operation);

        this.FillReasonForVisit();
    }

    private FillReasonForVisit(): void {
        var labfillerId = this.props.Model.get("LabMessage").Model.LabFillerID;
        var cleanLabFillerId = this.getlabFillerId(labfillerId);

        // Fill Raison of visit if its empty
        var operation = new LocalEvent(LocalOperations.Misc.ClickableTextControlAppend);
        operation.AddItem("value", cacheStore.GetString("ResultReviewText"));
        operation.AddItem("SpecificTextControlId", "lab_note_reason" + cleanLabFillerId);
        operation.AddItem("ApplyOnlyIfFieldIsNullOrEmpty", true);

        LocalEventDispatcher.dispatch(operation);
    }

    private showClickableText(clickableText: string): void {
        const operation = new LocalEvent(LocalOperations.LabsUI.ShowClickableText);
        operation.AddItem("value", clickableText);
        LocalEventDispatcher.dispatch(operation);
    }

    private hideClickableText(): void {
        const operation = new LocalEvent(LocalOperations.LabsUI.HideClickableText);
        LocalEventDispatcher.dispatch(operation);
    }

    private PortalStatusClicked(item: LabMessage): void {
        let operation = new LocalEvent(LocalOperations.LabsUI.PortalStatusClicked);
        operation.AddItem("lab", item);
        if (this.myleInfoPatient && this.myleInfoPatient.IndividualResponse) {
            operation.AddItem("registrationStatus", this.myleInfoPatient.IndividualResponse.PortalSyncState);
            operation.AddItem("patientContactId", this.myleInfoPatient.IndividualResponse.Contact_id);
        }

        LocalEventDispatcher.dispatch(operation);
    }

    private onNewTaskClick(): void {
        sessionStore.dispatch(
            LocalOperations.SendTaskOrMessageModel.Show_TaskOrMessage_Popup,
            {
                id: this.LabFillerID,
                target: MessageOrTaskContext.Labs,
                item: this.myleInfoPatient,
                lab: this.props.Model.get("LabMessage")
            }
        );
    }

    private onTaskTrailClick(e: React.MouseEvent): void {
        sessionStore.dispatch(
            LocalOperations.LabsUI.ShowAttachedCommunicationsPopup,
            {
                eventY: e.clientY,
                eventX: e.clientX
            }
        );
    }

    private onAttachedCommunicationClick(attachedCommunication: AttachedCommunication): void {
        sessionStore.dispatch(
            LocalPatientOperations.History.TaskRedirectFromEncounterOrLabs,
            {
                attachedCommunications: [attachedCommunication],
                isFromLab: true
            }
        );
    }

    private onAttachedCommunicationClose(): void {
        sessionStore.dispatch(LocalPatientOperations.History.AttachedCommunicationsClose);
    }

    private renderExternalCommunicationPopup(myleInfoPatient: Patient): JSX.Element {
        let selectedPatient = Person.FromPatient(myleInfoPatient);

        return (
            <ExternalMessageFormPopupComponent
                selectedPatient={InternalPatientResponse.fromPerson(selectedPatient)}
                onClose={() => this.showExternalCommunicationPopup(false)}
                onSend={() => this.showExternalCommunicationPopup(false)}
                selectedAttachments={this.props.Model.get("LabMessage") && this.props.Model.get("LabMessage").convertEncapsulatedDataToExportablePDF()}
            />
        );
    }

    private showExternalCommunicationPopup(show: boolean = true): void {
        sessionStore.dispatch(
            LocalOperations.LabsUI.ShowExternalCommunicationPopup,
            {
                show: show
            }
        );
    }

    private isConsultationElementFilled(): boolean {
        return (
            !!this.props.SideNoteModel.get("Consultation")
            || !!this.props.SideNoteModel.get("ConsultationDestinataire")
            || !!this.props.SideNoteModel.get("ConsultationSpeciality")
            || !!this.props.SideNoteModel.get("UrgentConsultation")
        );
    }

    private isConsultationSectionValid(): boolean {
        if (this.isConsultationElementFilled()) {
            return (
                !!this.props.SideNoteModel.get("Consultation")
                && !!this.props.SideNoteModel.get("ConsultationSpeciality")
            );
        }
        return true;
    }

    private isLabRequestSectionValid(): boolean {
        return (
            !this.props.SideNoteModel.get("UrgentRequete")
            || !!this.props.SideNoteModel.get("Requete")
        );
    }

    private openSecondScreen(): void {
        const patientId = this.props.Model.get("MylePatient").IndividualResponse.Contact_id;
        SecondScreenHelper.openWindow({ patientId });
    }

    private toggleMedicalSummaryPanel(): void {
        this.mutateState(s => s.isMedicalSummaryPanelOpen = !s.isMedicalSummaryPanelOpen, () => {
            MedicalSummaryPanelAction.updateMedicalSummaryPanelSettingsWithContext(
                cacheStore.UserSettings?.LabViewMedicalSummaryPanelSettings,
                this.state.isMedicalSummaryPanelOpen,
                MedicalSummaryPanelContext.PatientLabTab
            );
        });
    }

    private getlabFillerId(labFillerId: string): string {
        return this.myleHelper.RemoveDiacritics(MyleHelper.RemoveSpaces(labFillerId)).replaceAll("/", "").replaceAll(".", "");
    }

    private toggleCreatePatientPopupClick(): void {
        sessionStore.dispatch(LocalOperations.LabsUI.ToggleCreatePatientPopup);
    }

    private createNewPatient(patient: IPatientCreationModel): void {
        sessionStore.dispatch(LocalOperations.LabsUI.CreateNewPatientAndLinkResult, { patient });
    }

    private renderCreatePatientPopup(): JSX.Element {
        return (
            <CreatePatientPopup
                patientCreationModel={labsStore.LabModel.get("PatientCreationModel")}
                onSave={patient => this.createNewPatient(patient)}
                onCancel={() => this.toggleCreatePatientPopupClick()}
                customOkButtonLabel={cacheStore.GetString("LinkLabel")}
                customTitle={cacheStore.GetString("LinkPatientLabel").toTitleCase()}
                displayCreatePatientLabel={true}
            />
        );
    }
}

export { PatientLabView };
